/**
 * Telemetry Gaps, as the artefact its reader actually receives.
 *
 * An export rather than a view, and that is the whole placement argument. A
 * detection engineer takes this list, opens a ticket per requirement and works
 * through it over a quarter. Nobody watches it. It is also the one Exposure
 * surface whose reader does not live in the agent registry, which is what made
 * me reach for a page — and a page would have been "a destination for it was a
 * page nobody had a reason to open" all over again.
 *
 * What must survive the export
 * ----------------------------
 * The disclaimer. The payload carries its own, because the whole capability is
 * a refusal: ARIAS does not generate Sigma, KQL or SPL, since an unvalidated
 * rule in a SIEM is indistinguishable from a working one while providing no
 * coverage. A CSV that drops it is a list of requirements a recipient could
 * reasonably mistake for detection content — which is precisely the failure the
 * backend refuses to commit.
 *
 * So the disclaimer, the ATLAS release and the schema basis are written as
 * header lines above the table. They are dated claims about two moving schemas,
 * and a file read in six months needs to know which version it was true of.
 *
 * Both axes travel too. `schema_fit` says whether a field MEANS this;
 * `source` says whether anything POPULATES it. Exporting only the first would
 * tell a reader the work is a query when it is usually an ingestion project.
 */
import { useState } from 'react'
import { Button, Card, CardContent, Typography } from '@mui/material'
import { Sensors } from '@mui/icons-material'
import { attackPathsApi } from '../services/api'

/** One CSV field, quoted and escaped. Commas and newlines are everywhere in
 *  this payload — every `observable` and `limit` is a sentence. */
const cell = (v: unknown) =>
  `"${(v ?? '').toString().replace(/"/g, '""')}"`

const TelemetryGapsExport = ({ analysisId }: { analysisId?: string }) => {
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState(false)

  const download = async () => {
    setBusy(true)
    try {
      const { data } = await attackPathsApi.telemetryGaps()
      const lines: string[] = []

      // Header lines before the table. A spreadsheet shows them as leading
      // rows, which is clumsy and still better than a recipient believing
      // these are rules.
      lines.push(cell('ARIAS Telemetry Gaps — observability requirements, NOT detection rules'))
      lines.push(cell(data.disclaimer))
      if (data.headline) lines.push(cell(data.headline))
      lines.push(cell(`ATLAS reference: ${data.atlas_release}`))
      lines.push(cell(`Schema basis: ${data.schema_basis}`))
      lines.push(cell(`Scope: ${data.scope}`))
      lines.push('')

      lines.push([
        'Requirement', 'What you should be able to see', 'Correlation',
        'Effort', 'Why that effort', 'OCSF fit', 'OCSF source', 'OCSF target',
        'OCSF loses', 'ASIM fit', 'ASIM source', 'ASIM target', 'ASIM loses',
        'ATLAS', 'Limit', 'Triggered by',
      ].map(cell).join(','))

      for (const r of data.required || []) {
        lines.push([
          r.key, r.observable, r.correlation,
          r.effort?.verdict, r.effort?.explanation,
          r.ocsf?.fit, r.ocsf?.source, r.ocsf?.target, r.ocsf?.loss,
          r.asim?.fit, r.asim?.source, r.asim?.target, r.asim?.loss,
          // Each id with whether it has been verified against the release
          // above. If an id is wrong the requirement is still right, and a
          // reader can see which part to distrust.
          (r.atlas_techniques || [])
            .map((t: { id: string; name?: string; verified?: boolean }) =>
              `${t.id}${t.name ? ` (${t.name})` : ''}${t.verified === false ? ' [unverified]' : ''}`)
            .join('; '),
          r.limit,
          r.triggered_by?.reason,
        ].map(cell).join(','))
      }

      // Not required, and why — because "we looked and your estate does not
      // need this" and "we did not consider it" are different statements, and
      // only the first is worth anything to a recipient.
      if ((data.not_required || []).length) {
        lines.push('')
        lines.push(cell('Not required for this estate'))
        lines.push(['Requirement', 'What it would cover', 'Why not required']
          .map(cell).join(','))
        for (const r of data.not_required) {
          lines.push([r.key, r.observable, r.reason].map(cell).join(','))
        }
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `arias-telemetry-gaps-${(analysisId || 'estate').substring(0, 8)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // A 404 is the ordinary case for a tenant without Exposure.
      setRefused(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ textAlign: 'center' }}>
        <Sensors sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
        <Typography variant="subtitle2" gutterBottom>
          Export Telemetry Gaps
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What this estate means you should be able to see in telemetry, which
          ASIM or OCSF field would carry it, and whether anything populates that
          field. Requirements, not rules.
        </Typography>
        {refused ? (
          <Typography variant="caption" color="text.secondary">
            Included with ARIAS Exposure.
          </Typography>
        ) : (
          <Button variant="outlined" onClick={download} disabled={busy}>
            {busy ? 'Preparing…' : 'Download CSV'}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

export default TelemetryGapsExport
